import { diffCriteria } from '@/lib/rubric-publish-payload'
import { RubricArtifactCanvasLayout } from '@/components/admin/evaluation-rubrics/rubric-artifact-canvas-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { RubricCriterion, RubricRevisionSummary } from '@/hooks/use-evaluation-rubrics-admin'

type RubricRevisionHistoryCanvasProps = {
  revisions: RubricRevisionSummary[]
  publishedCriteria: RubricCriterion[]
  draftCriteria: RubricCriterion[]
  onClose: () => void
}

export function RubricRevisionHistoryCanvas({
  revisions,
  publishedCriteria,
  draftCriteria,
  onClose,
}: RubricRevisionHistoryCanvasProps) {
  const criteriaDiff = diffCriteria(publishedCriteria, draftCriteria)

  return (
    <RubricArtifactCanvasLayout
      actions={
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      {hasUnpublishedChanges(criteriaDiff) ? (
        <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-2">
          <p className="font-medium">Unpublished draft changes</p>
          {criteriaDiff.added.length > 0 ? (
            <p>Added: {criteriaDiff.added.map((row) => row.label || row.key).join(', ')}</p>
          ) : null}
          {criteriaDiff.removed.length > 0 ? (
            <p>Removed: {criteriaDiff.removed.map((row) => row.label || row.key).join(', ')}</p>
          ) : null}
          {criteriaDiff.changed.length > 0 ? (
            <p>Changed: {criteriaDiff.changed.map((row) => row.key).join(', ')}</p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No unpublished criteria changes in draft.</p>
      )}

      {revisions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No revisions yet.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {revisions.map((revision) => (
            <li
              key={revision.id}
              className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
            >
              <span className="font-medium">v{revision.version}</span>
              <Badge variant="outline">{revision.provenance}</Badge>
              <span className="text-muted-foreground">
                {new Date(revision.created_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </RubricArtifactCanvasLayout>
  )
}

function hasUnpublishedChanges(diff: ReturnType<typeof diffCriteria>) {
  return diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0
}
